import { PropsWithChildren, useState, useEffect } from 'react';
import { makeStyles, Box } from '@material-ui/core';
import HomeIcon from '@material-ui/icons/Home';
import ExtensionIcon from '@material-ui/icons/Extension';
import CreateComponentIcon from '@material-ui/icons/AddCircleOutline';
import { MyCustomLogoFull, MyCustomLogoIcon } from './LogoComponents';
import { TDLogoFull, TDLogoIcon, TDLogoFullDark, TDLogoIconDark } from './TDLogoComponents';
import { SidebarSearchModal } from '@backstage/plugin-search';
import {
  Sidebar,
  SidebarDivider,
  SidebarGroup,
  SidebarItem,
  SidebarPage,
  SidebarScrollWrapper,
  SidebarSpace,
  useSidebarOpenState,
  Link,
} from '@backstage/core-components';
import MenuIcon from '@material-ui/icons/Menu';
import SearchIcon from '@material-ui/icons/Search';
import { MyGroupsSidebarItem } from '@backstage/plugin-org';
import GroupIcon from '@material-ui/icons/People';
import CloudIcon from '@material-ui/icons/Cloud';
import GitHubIcon from '@material-ui/icons/GitHub';
import LockIcon from '@material-ui/icons/Lock';
import BuildIcon from '@material-ui/icons/Build';
import CodeIcon from '@material-ui/icons/Code';
import BrushIcon from '@material-ui/icons/Brush';
import FlareIcon from '@material-ui/icons/Flare';
import CategoryIcon from '@material-ui/icons/Category';
import AccountTreeIcon from '@material-ui/icons/AccountTree';
import { UserProfile } from './UserProfile';
import { SidebarAuthStatus } from './SidebarAuthStatus';
import { GcpConnectionBanner } from './GcpConnectionBanner';
import { useApi, appThemeApiRef, configApiRef, discoveryApiRef, fetchApiRef } from '@backstage/core-plugin-api';
import './SidebarActiveStyles.css';

const useSidebarLogoStyles = makeStyles({
  root: {
    width: '100%',
    height: 'auto',
    display: 'flex',
    flexFlow: 'row nowrap',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 0,
    padding: '16px 0',
  },
  link: {
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
});

const SidebarLogo = () => {
  const classes = useSidebarLogoStyles();
  const { isOpen } = useSidebarOpenState();
  const appThemeApi = useApi(appThemeApiRef);
  const [currentTheme, setCurrentTheme] = useState<string>('');

  useEffect(() => {
    // Get the current theme
    const subscription = appThemeApi.activeThemeId$().subscribe((themeId) => {
      setCurrentTheme(themeId || '');
    });

    return () => subscription.unsubscribe();
  }, [appThemeApi]);

  // Determine which logo to show based on the current theme
  const getLogoComponent = () => {
    if (currentTheme.startsWith('td-')) {
      // TD themes
      if (currentTheme === 'td-dark') {
        return isOpen ? <TDLogoFullDark /> : <TDLogoIconDark />;
      } else {
        return isOpen ? <TDLogoFull /> : <TDLogoIcon />;
      }
    } else {
      // Badal themes (default)
      return isOpen ? <MyCustomLogoFull /> : <MyCustomLogoIcon />;
    }
  };

  return (
    <div className={classes.root}>
      <Link to="/" underline="none" className={classes.link} aria-label="Home">
        {getLogoComponent()}
      </Link>
    </div>
  );
};

const useSidebarVersionStyles = makeStyles(theme => ({
  version: {
    textAlign: 'center' as const,
    fontSize: '0.7rem',
    color: (theme.palette as any).navigation?.color || '#FFFFFF',
    opacity: 0.5,
    marginTop: -8,
    marginBottom: 4,
    textDecoration: 'none',
    display: 'block',
    '&:hover': {
      opacity: 0.85,
    },
  },
}));

const SidebarVersion = () => {
  const classes = useSidebarVersionStyles();
  const { isOpen } = useSidebarOpenState();
  const config = useApi(configApiRef);
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const [version, setVersion] = useState<string | null>(null);

  const repoUrl = config.getOptionalString('app.repositoryUrl');

  useEffect(() => {
    let cancelled = false;
    discoveryApi.getBaseUrl('app-info').then(baseUrl =>
      fetchApi.fetch(`${baseUrl}/`).then(res => {
        if (!cancelled && res.ok) {
          res.json().then(data => {
            if (!cancelled) setVersion(data.version);
          });
        }
      }),
    ).catch(() => {
      // Silently ignore — version display is non-critical
    });
    return () => { cancelled = true; };
  }, [discoveryApi, fetchApi]);

  if (!isOpen || !version) return null;

  if (repoUrl) {
    const treeRef = version.match(/^\d+\./) ? `v${version}` : version;
    const href = `${repoUrl}/tree/${encodeURIComponent(treeRef)}`;

    return (
      <a className={classes.version} href={href} target="_blank" rel="noopener noreferrer">
        {version}
      </a>
    );
  }

  return <div className={classes.version}>{version}</div>;
};

export const Root = ({ children }: PropsWithChildren<{}>) => {
  return (
  <SidebarPage>
    <Sidebar>
      <SidebarLogo />
      <SidebarVersion />
      <SidebarGroup label="Search" icon={<SearchIcon />} to="/search">
        <SidebarSearchModal />
      </SidebarGroup>
      <SidebarDivider />
      <SidebarGroup label="Menu" icon={<MenuIcon />}>
        {/* Global nav, not org-specific */}
        <SidebarItem icon={HomeIcon} to="/" text="Home" />
        <SidebarItem icon={CategoryIcon} to="catalog" text="Catalog" />
        <SidebarItem icon={AccountTreeIcon} to="foundation" text="Foundation" />
        <MyGroupsSidebarItem
          singularTitle="My Group"
          pluralTitle="My Groups"
          icon={GroupIcon}
        />
        <SidebarItem icon={ExtensionIcon} to="api-docs" text="APIs" />
        <SidebarItem icon={CloudIcon} to="gcp-projects" text="GCP Projects" />
        <SidebarItem icon={GitHubIcon} to="github-repositories" text="GitHub Repos" />
        {/* Encrypted Secrets - DISABLED */}
        {/* <SidebarItem icon={LockIcon} to="stored-credentials" text="Stored Credentials" /> */}
        <SidebarItem icon={LockIcon} to="vault-secrets" text="Vault Secrets" />
        <SidebarItem icon={BuildIcon} to="terraform-cloud" text="Terraform Cloud" />
        <SidebarItem icon={CodeIcon} to="claude-flow" text="Claude Flow" />
        <SidebarItem icon={FlareIcon} to="gemini-agent" text="Gemini Agent" />
        {/* <SidebarItem icon={LibraryBooks} to="docs" text="Docs" /> */}
        <SidebarItem icon={CreateComponentIcon} to="create" text="Create..." />
        <SidebarItem icon={BrushIcon} to="mockup" text="UI Mockup" />
        {/* End global nav */}
        <SidebarScrollWrapper>
          {/* Items in this group will be scrollable if they run out of space */}
        </SidebarScrollWrapper>
      </SidebarGroup>
      <SidebarSpace />
      <SidebarDivider />
      <SidebarAuthStatus />
      <SidebarDivider />
      <UserProfile />
    </Sidebar>
    <Box style={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 }}>
      <GcpConnectionBanner />
      {children}
    </Box>
  </SidebarPage>
  );
};
